<template>
    <main class="p-2 md:p-5 w-full h-full max-h-main flex flex-col items-start min-h-main">
        <div class="w-full flex flex-col gap-2">
            <div class="flex w-full sm:flex-row gap-2">
                <app-button @click="newSubscription()" :label="$t('subscriptions.newSubscription')" />
            </div>
            <app-filter ref="filter" v-model="filter"></app-filter>
            <div class="flex justify-between">
                <div class="w-full flex flex-col justify-start gap-1 sm:gap-4 sm:justify-center sm:flex-row text-sm">
                    <app-checkbox
                        class="justify-end sm:justify-center"
                        v-model="active"
                        :label="$t('subscriptions.active')"
                        @input="saveFilter"
                    ></app-checkbox>
                    <app-checkbox
                        class="justify-end sm:justify-center"
                        v-model="waiting"
                        :label="$t('subscriptions.waiting')"
                        @input="saveFilter"
                    ></app-checkbox>
                </div>
            </div>
        </div>
        <div class="flex w-full flex-grow overflow-auto flex-col text-xs relative">
            <div v-if="loading" class="flex justify-center">
                <icon-rotate-right class="animate animate-spin"></icon-rotate-right>
            </div>
            <table class="table-fixed" v-else>
                <thead class="sticky top-0 bg-white">
                    <tr>
                        <th style="width: 4rem" class="text-left border-r p-1">
                            {{ $t('subscriptions.code') }}
                        </th>
                        <th style="width: 5rem" class="text-left border-r p-1">
                            <button
                                class="hover:underline font-bold w-full flex justify-between"
                                @click="sortBy('createdAt')"
                            >
                                {{ $t('subscriptions.validatedAt') }}
                                <div v-if="sortKey === 'validatedAt'">
                                    <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                    <icon-menu-down v-else width="16" height="16" />
                                </div>
                            </button>
                        </th>
                        <th style="width: 5rem" class="text-left border-r p-1 bg-white">
                            <button
                                class="hover:underline font-bold w-full flex justify-between"
                                @click="sortBy('startDate')"
                            >
                                <span>{{ $t('subscriptions.startDate') }}</span>
                                <div v-if="sortKey === 'startDate'">
                                    <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                    <icon-menu-down v-else width="16" height="16" />
                                </div>
                            </button>
                        </th>
                        <th style="width: 5rem" class="text-left border-r p-1 bg-white">
                            <button
                                class="hover:underline font-bold w-full flex justify-between"
                                @click="sortBy('endDate')"
                            >
                                <span>{{ $t('subscriptions.endDate') }}</span>
                                <div v-if="sortKey === 'endDate'">
                                    <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                    <icon-menu-down v-else width="16" height="16" />
                                </div>
                            </button>
                        </th>
                        <th class="text-left border-r p-1">
                            <button
                                class="hover:underline font-bold w-full flex justify-between"
                                @click="sortBy('owner')"
                            >
                                {{ $t('subscriptions.owner') }}
                                <div v-if="sortKey === 'owner'">
                                    <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                    <icon-menu-down v-else width="16" height="16" />
                                </div>
                            </button>
                        </th>
                        <th class="text-left border-r p-1">
                            <button
                                class="hover:underline font-bold w-full flex justify-between"
                                @click="sortBy('organization')"
                            >
                                {{ $t('subscriptions.organization') }}
                                <div v-if="sortKey === 'organization'">
                                    <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                    <icon-menu-down v-else width="16" height="16" />
                                </div>
                            </button>
                        </th>
                        <th style="width: 5rem" class="text-left border-r p-1">
                            {{ $t('subscriptions.status') }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="subscription in filteredItems">
                        <tr class="odd:bg-blue-50 border-t">
                            <td class="border-r p-1 underline">
                                <router-link
                                    :to="{
                                        name: 'subscription',
                                        params: {
                                            subscriptionId: subscription.id,
                                        },
                                    }"
                                >
                                    {{ subscription.code }}
                                </router-link>
                            </td>
                            <td class="border-r p-1">
                                {{ subscription.validatedAt | humanizeDate }}
                            </td>
                            <td class="border-r p-1">
                                {{ subscription.startDate | humanizeDate }}
                            </td>
                            <td class="border-r p-1">
                                {{ subscription.endDate | humanizeDate }}
                            </td>
                            <td class="border-r p-1">
                                {{ subscription.owner.email }}
                            </td>
                            <td class="border-r p-1">
                                {{ subscription.organization.name }}
                                <span v-if="subscription.organization.city">
                                    ({{ subscription.organization.city }})
                                </span>
                            </td>
                            <td class="border-r p-1">
                                <span v-if="subscription.validatedAt">{{ $t('subscriptions.active') }}</span>
                                <span v-else>{{ $t('subscriptions.waiting') }}</span>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
        <app-autocomplete
            ref="newSubscription"
            :value="selectedOrganization"
            @input="createSubscription($event)"
            :options="organizations"
            :allowStringCriteria="false"
            :filter-string-function="filterOrganizations"
        >
            <template v-slot:title>
                <app-separator :label="$t('subscriptions.chooseOrganization')" />
            </template>
            <template v-slot:item="{ item }">
                <span class="p-1">{{ item.siret }} - {{ item.name }} ({{ item.city }}) - {{ item.owner.email }}</span>
            </template>
        </app-autocomplete>
    </main>
</template>

<script>
import { filterMatch, sortBy } from '@/services/sanitize.service';
import AppSelect from '@/components/appSelect/AppSelect';
import { createSubscription, getSubscriptions } from './subscriptions.service';
import AppPopup from '@/components/app-popup/AppPopup';
import AppFooter from '@/components/appFooter/AppFooter';
import AppButton from '@/components/appButton/AppButton';
import AppInputText from '@/components/appInputText/AppInputText.vue';
import AppFilter from '@/components/appFilter/AppFilter.vue';
import AppCheckbox from '@/components/app-checkbox/AppCheckbox.vue';
import { error } from '@/features/toasts/toats.service';
import AppAutocomplete from '@/components/app-autocomplete/AppAutocomplete.vue';
import AppSeparator from '@/components/appSeparator/AppSeparator.vue';
import { getOrganizations } from '@/features/organizations/organizations.service';

export default {
    components: {
        AppSeparator,
        AppAutocomplete,
        AppCheckbox,
        AppFilter,
        AppInputText,
        AppSelect,
        AppFooter,
        AppPopup,
        AppButton,
    },
    async created() {
        this.restoreFilter();
        this.subscriptions = await getSubscriptions();
        this.organizations = await getOrganizations();
        this.loading = false;
    },
    computed: {
        filteredItems() {
            let result = this.filterFn(this.filter);
            if (this.sortKey) {
                result = sortBy(result, (item) => {
                    if (this.sortKey === 'startDate') {
                        return item.startDate;
                    } else if (this.sortKey === 'endDate') {
                        return item.endDate;
                    } else if (this.sortKey === 'owner') {
                        return item.owner.email;
                    } else if (this.sortKey === 'organization') {
                        return item.organization.name;
                    }
                });
                if (!this.sortAsc) {
                    result.reverse();
                }
            }
            return result;
        },
    },
    methods: {
        sortBy(key) {
            if (key === this.sortKey) {
                this.sortAsc = !this.sortAsc;
            } else {
                this.sortKey = key;
                this.sortAsc = true;
            }
        },
        saveFilter() {
            localStorage.setItem(
                'subscriptions_filter',
                JSON.stringify({
                    filter: this.filter,
                    sortAsc: this.sortAsc,
                    sortKey: this.sortKey,
                    waiting: this.waiting,
                    active: this.active,
                }),
            );
        },
        restoreFilter() {
            const cache = localStorage.getItem('subscriptions_filter');
            if (cache) {
                const parsedCache = JSON.parse(cache);
                this.sortAsc = !!parsedCache.sortAsc;
                this.sortKey = parsedCache.sortKey || 'validatedAt';
                this.filter = parsedCache.filter || '';
                this.waiting = !!parsedCache.waiting;
                this.active = !!parsedCache.active;
            }
        },
        filterOrganizations(item) {
            return item.siret + item.name + item.city + item.owner.email + item.owner.lastName;
        },
        async createSubscription(organization) {
            try {
                const date = new Date();
                const result = await createSubscription({
                    organizationId: organization.id,
                    startDate: new Date(date.getFullYear(), date.getMonth() + 1, 0),
                    endDate: new Date(date.getFullYear() + 1, date.getMonth() + 1, 0),
                    type: 'pro',
                    firstPaymentAt: new Date(date.getFullYear(), date.getMonth() + 1, 0),
                    billingFrequency: 'monthly',
                    creditAmount: 10000,
                });
                await this.$router.push({
                    name: 'subscription',
                    params: {
                        subscriptionId: result.id,
                    },
                });
            } catch (e) {
                error(this.$t('commons.error'));
            }
        },
        filterFn(filter) {
            this.saveFilter();
            return this.subscriptions.filter((item) => {
                const filterString = [item.organization.name, item.owner.email, item.code, item.type].join();
                return (
                    filterMatch(filterString, filter, true) &&
                    (!item.validatedAt || this.active) &&
                    (!!item.validatedAt || this.waiting)
                );
            });
        },
        newSubscription() {
            this.$refs.newSubscription.open();
        },
    },
    data() {
        return {
            filter: '',
            waiting: true,
            active: true,
            sortKey: 'validatedAt',
            sortAsc: false,
            loading: true,
            readOnly: true,
            subscriptions: [],
            organizations: [],
            selectedOrganization: [],
        };
    },
};
</script>
